/**
 * Turns the raw store data into a small, factual PortfolioSnapshot.
 *
 * Everything the assistant says about the landlord's portfolio comes from
 * here: deterministic answers read it directly, and the LLM only ever sees
 * it rendered as a short context string (contextBuilder.ts). Pure and
 * synchronous, so the numbers are unit-testable without stores or network.
 */
import { daysBetween } from './format';
import type {
  AssistantSourceData,
  ContractLike,
  EndingLease,
  InvoiceLike,
  OverdueInvoice,
  OverdueTenant,
  PersonLike,
  PlotLike,
  PortfolioSnapshot,
  StatusLike,
} from './types';

export const LEASE_WINDOW_DAYS = 90;
export const SCHEDULE_WINDOW_DAYS = 14;
const PAYMENT_WINDOW_DAYS = 30;

const ACTIVE_CONTRACT = new Set(['active', 'signed', 'renewal-requested', 'in closure']);
const IGNORED_INVOICE = new Set(['draft', 'cancelled']);

type StatusName = (id: number | undefined) => string;

function statusNamer(statuses: StatusLike[]): StatusName {
  const byId = new Map(statuses.map((s) => [s.id, (s.name ?? '').trim().toLowerCase()]));
  return (id) => (id === undefined ? '' : byId.get(id) ?? '');
}

function fullName(person: PersonLike | null | undefined): string {
  if (!person) return '';
  return `${person.first_name ?? ''} ${person.last_name ?? ''}`.trim();
}

function isContractActive(c: ContractLike, status: StatusName, today: string): boolean {
  const name = status(c.status_id);
  if (name) return ACTIVE_CONTRACT.has(name);
  // Unknown status: fall back to the lease dates.
  return c.start_date.slice(0, 10) <= today && c.end_date.slice(0, 10) >= today;
}

function isContractUpcoming(c: ContractLike, status: StatusName, today: string): boolean {
  const name = status(c.status_id);
  if (name) return name === 'upcoming';
  return c.start_date.slice(0, 10) > today;
}

function plotSummary(plots: PlotLike[], status: StatusName, today: string): PortfolioSnapshot['plots'] {
  let occupied = 0;
  let vacant = 0;
  let archived = 0;
  const vacantPlots: PortfolioSnapshot['plots']['vacantPlots'] = [];
  for (const plot of plots) {
    const name = status(plot.status_id);
    const hasLease = (plot.contracts ?? []).some((c) => isContractActive(c, status, today));
    const kind = name === 'archived' ? 'archived' : name === 'occupied' || (!name && hasLease) ? 'occupied' : 'vacant';
    if (kind === 'archived') archived += 1;
    else if (kind === 'occupied') occupied += 1;
    else {
      vacant += 1;
      vacantPlots.push(plot.size ? { title: plot.title, size: plot.size } : { title: plot.title });
    }
  }
  const total = occupied + vacant;
  return {
    total,
    occupied,
    vacant,
    archived,
    occupancyRate: total > 0 ? Math.round((occupied / total) * 100) : null,
    vacantPlots,
  };
}

interface Lookups {
  tenantForContract: (contractId: number, contactId?: number) => string;
  plotForContract: (contractId: number) => string;
}

function buildLookups(data: AssistantSourceData): Lookups {
  const contracts = new Map(data.contracts.map((c) => [c.id, c]));
  const contacts = new Map(data.contacts.filter((p) => p.id !== undefined).map((p) => [p.id as number, p]));
  const plots = new Map(data.plots.map((p) => [p.id, p.title]));
  return {
    tenantForContract: (contractId, contactId) => {
      const contract = contracts.get(contractId);
      const id = contactId ?? contract?.contact_id;
      return fullName(contract?.contact) || fullName(id !== undefined ? contacts.get(id) : undefined) || 'A tenant';
    },
    plotForContract: (contractId) => {
      const contract = contracts.get(contractId);
      if (!contract) return '';
      return contract.plot?.title || plots.get(contract.plot_id) || '';
    },
  };
}

function leaseSummary(data: AssistantSourceData, status: StatusName, today: string, lookups: Lookups): PortfolioSnapshot['leases'] {
  const active = data.contracts.filter((c) => isContractActive(c, status, today));
  const endingSoon: EndingLease[] = active
    .map((c) => ({
      tenant: lookups.tenantForContract(c.id, c.contact_id),
      plot: lookups.plotForContract(c.id),
      endDate: c.end_date.slice(0, 10),
      daysLeft: daysBetween(today, c.end_date),
    }))
    .filter((l) => l.daysLeft >= 0 && l.daysLeft <= LEASE_WINDOW_DAYS)
    .sort((a, b) => a.daysLeft - b.daysLeft);
  return {
    active: active.length,
    upcoming: data.contracts.filter((c) => isContractUpcoming(c, status, today)).length,
    windowDays: LEASE_WINDOW_DAYS,
    endingSoon,
  };
}

function isOverdue(inv: InvoiceLike, status: StatusName, today: string): boolean {
  if (!(inv.balance_due > 0)) return false;
  const name = status(inv.status_id);
  if (name === 'paid') return false;
  return name === 'overdue' || inv.due_date.slice(0, 10) < today;
}

function groupByTenant(overdue: OverdueInvoice[]): OverdueTenant[] {
  const groups = new Map<string, OverdueTenant>();
  for (const inv of overdue) {
    const prev = groups.get(inv.tenant);
    groups.set(inv.tenant, {
      tenant: inv.tenant,
      balance: (prev?.balance ?? 0) + inv.balance,
      invoiceCount: (prev?.invoiceCount ?? 0) + 1,
      oldestDaysOverdue: Math.max(prev?.oldestDaysOverdue ?? 0, inv.daysOverdue),
    });
  }
  return [...groups.values()].sort((a, b) => b.balance - a.balance);
}

function invoiceSummary(data: AssistantSourceData, status: StatusName, today: string, lookups: Lookups): PortfolioSnapshot['invoices'] {
  const counted = data.invoices.filter((inv) => !IGNORED_INVOICE.has(status(inv.status_id)));
  const billed = counted.reduce((sum, inv) => sum + (inv.total_amount || 0), 0);
  const outstanding = counted.reduce((sum, inv) => sum + Math.max(inv.balance_due || 0, 0), 0);
  const collected = billed - outstanding;
  const overdue: OverdueInvoice[] = counted
    .filter((inv) => isOverdue(inv, status, today))
    .map((inv) => ({
      invoiceNumber: inv.invoice_number || `#${inv.id}`,
      tenant: lookups.tenantForContract(inv.contract_id, inv.contact_id),
      plot: lookups.plotForContract(inv.contract_id),
      balance: inv.balance_due,
      dueDate: inv.due_date.slice(0, 10),
      daysOverdue: Math.max(daysBetween(inv.due_date, today), 0),
    }))
    .sort((a, b) => b.daysOverdue - a.daysOverdue);
  return {
    count: counted.length,
    billed,
    collected,
    outstanding,
    collectionRate: billed > 0 ? Math.round((collected / billed) * 100) : null,
    overdue,
    overdueTotal: overdue.reduce((sum, inv) => sum + inv.balance, 0),
    overdueByTenant: groupByTenant(overdue),
  };
}

function paymentSummary(data: AssistantSourceData, today: string): PortfolioSnapshot['payments'] {
  const recent = data.payments.filter((p) => {
    const age = daysBetween(p.payment_date, today);
    return age >= 0 && age <= PAYMENT_WINDOW_DAYS;
  });
  return {
    last30DaysTotal: recent.reduce((sum, p) => sum + (p.amount || 0), 0),
    last30DaysCount: recent.length,
  };
}

function scheduleSummary(data: AssistantSourceData, today: string): PortfolioSnapshot['schedule'] {
  const upcoming = data.activities
    .filter((a) => a.start_date)
    .map((a) => ({ title: a.title, date: (a.start_date as string).slice(0, 10) }))
    .filter((a) => {
      const ahead = daysBetween(today, a.date);
      return ahead >= 0 && ahead <= SCHEDULE_WINDOW_DAYS;
    })
    .sort((a, b) => a.date.localeCompare(b.date));
  return { total: data.activities.length, upcoming, windowDays: SCHEDULE_WINDOW_DAYS };
}

export function buildSnapshot(data: AssistantSourceData, today: string): PortfolioSnapshot {
  const status = statusNamer(data.statuses);
  const lookups = buildLookups(data);
  return {
    today,
    plots: plotSummary(data.plots, status, today),
    leases: leaseSummary(data, status, today, lookups),
    invoices: invoiceSummary(data, status, today, lookups),
    payments: paymentSummary(data, today),
    leads: {
      total: data.leads.length,
      unread: data.leads.filter((l) => status(l.status_id) === 'unread' || l.read === false).length,
    },
    proposals: {
      total: data.proposals.length,
      inReview: data.proposals.filter((p) => ['in review', 'under review'].includes(status(p.status_id))).length,
      approved: data.proposals.filter((p) => status(p.status_id) === 'approved').length,
    },
    schedule: scheduleSummary(data, today),
  };
}
